import { useState, useCallback } from 'react';
import { getFile } from '../services/storage';
import { getChangedFiles, clearChangedFiles } from '../services/storage';
import { publishFile, getUserProfile } from '../services/api';
import { cleanHtmlForPublish } from '../services/editor';
import { canSubmitUpload } from '../utils/general';
import { isHtmlFile } from '../utils/path';

export function usePublishing(domain, onSuccess) {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const publish = useCallback(async () => {
    setIsPublishing(true);

    try {
      const profile = await getUserProfile();
      const canUpdate = canSubmitUpload(profile);

      if (!canUpdate) {
        setUserProfile(profile);
        setShowUpgradeModal(true);
        setIsPublishing(false);
        return { success: false, reason: 'limit' };
      }

      const changedFiles = getChangedFiles();

      for (const file of changedFiles) {
        const content = await getFile(file);
        const finalContent = isHtmlFile(file) ? cleanHtmlForPublish(content) : content;
        await publishFile(file, finalContent, domain);
      }

      clearChangedFiles();
      setIsPublishing(false);
      onSuccess?.();
      return { success: true };
    } catch (err) {
      console.error('Publish error:', err);
      setIsPublishing(false);
      return { success: false, reason: 'error', error: err };
    }
  }, [domain, onSuccess]);

  const discard = useCallback(async (refetchFiles) => {
    setIsDiscarding(true);

    try {
      clearChangedFiles();
      if (refetchFiles) {
        await refetchFiles();
      }
      onSuccess?.();
    } catch (err) {
      console.error('Discard error:', err);
    }

    setIsDiscarding(false);
  }, [onSuccess]);

  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
  }, []);

  return {
    // State
    isPublishing,
    isDiscarding,
    showUpgradeModal,
    userProfile,

    // Actions
    publish,
    discard,
    closeUpgradeModal
  };
}
